import { Avatar, Typography, Button, Tooltip, theme } from 'antd'
import { ArrowLeftOutlined, SearchOutlined, CloseOutlined, TeamOutlined, InfoCircleOutlined, ExportOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../Stores'
import { formatLastSeen } from '../../Helpers/formatDate'
import { CHAT_PRESENCE_ENABLED } from '../../Helpers/chatFeatures'
import OnlineIndicator from '../../Components/common/OnlineIndicator'

const { Text } = Typography

const ChatHeader = observer(() => {
  const { uiStore, chatStore, userStore } = useStore()
  const { token } = theme.useToken()

  const chat = chatStore.activeChat
  if (!chat) return null

  const displayName = chatStore.getChatDisplayName(chat)
  const avatarUrl = chatStore.getChatAvatar(chat)
  const isSearchOpen = uiStore.isMessageSearchOpen

  // Status line
  let statusText: string
  let isOnline = false

  if (chat.type === 'group') {
    statusText = `${chat.participantIds.length} учасників`
  } else if (!CHAT_PRESENCE_ENABLED) {
    // Presence (online/lastSeen) is a phase-2 backend feature
    statusText = ''
  } else {
    const otherId = chat.participantIds.find((id: string) => id !== userStore.currentUserId)
    const otherUser = otherId ? userStore.getById(otherId) : undefined
    isOnline = otherUser?.status === 'online'
    statusText = isOnline ? 'онлайн' : otherUser ? formatLastSeen(otherUser.lastSeen) : ''
  }

  // «Друкує…» тимчасово витісняє статус (формулювання — у ChatStore.typingLabel)
  const typingLabel = chatStore.typingLabel(chat, true)
  const isTyping = typingLabel !== null
  if (typingLabel !== null) {
    statusText = typingLabel
  }

  const handleSearchToggle = () => {
    if (isSearchOpen) {
      uiStore.closeMessageSearch()
    } else {
      uiStore.openMessageSearch()
    }
  }

  // Головна відмінність entity-чатів: місток назад до сутності.
  // Dynamic import — RequestResource сам імпортує наш фасад openEntityChat,
  // статичний імпорт звідси замкнув би модульний цикл
  const openBoundEntity = async () => {
    if (chat.entityType !== 'transport_request' || !chat.entityUuid) return

    // Віконна логіка: картка, відкрита З чату, лягає ПОВЕРХ чату;
    // клік по чату потім підніме його назад (див. ChatDrawer)
    uiStore.sendChatBehindResources()

    const [{ OpenResourceCardTask }, { RequestResource }] = await Promise.all([
      import('@/Core/Tasks/Resources/OpenResourceCardTask'),
      import('@/Containers/RequestSection/RequestContainer/Resources/RequestResource'),
    ])

    void OpenResourceCardTask({
      resource: RequestResource,
      primaryFieldValue: chat.entityUuid,
      mode: 'view',
    })
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 16px',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        gap: 12,
      }}
    >
      {/* Back button — mobile only */}
      {uiStore.isMobile && (
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => uiStore.setActiveChatId(null)}
          style={{ marginRight: -4 }}
        />
      )}

      {/* Avatar */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <Avatar
          src={avatarUrl}
          size={40}
          icon={chat.type === 'group' ? <TeamOutlined /> : undefined}
        >
          {!avatarUrl && displayName.charAt(0)}
        </Avatar>
        {isOnline && <OnlineIndicator />}
      </div>

      {/* Name + status */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ fontSize: 15, display: 'block', lineHeight: '20px' }}>
          {displayName}
        </Text>
        <Text
          type="secondary"
          style={{
            fontSize: 12,
            lineHeight: '16px',
            color: isTyping ? token.colorPrimary : isOnline ? '#52c41a' : undefined,
          }}
        >
          {statusText}
        </Text>
      </div>

      {/* Actions */}
      {chat.entityType === 'transport_request' && chat.entityUuid && (
        <Tooltip title="Відкрити заявку">
          <Button
            type="text"
            icon={<ExportOutlined />}
            onClick={() => void openBoundEntity()}
          />
        </Tooltip>
      )}
      <Tooltip title={isSearchOpen ? 'Закрити пошук' : 'Пошук повідомлень'}>
        <Button
          type="text"
          icon={isSearchOpen ? <CloseOutlined /> : <SearchOutlined />}
          onClick={handleSearchToggle}
        />
      </Tooltip>
      <Tooltip title="Про чат">
        <Button
          type="text"
          icon={<InfoCircleOutlined />}
          onClick={() => uiStore.toggleChatInfo()}
        />
      </Tooltip>
    </div>
  )
})

export default ChatHeader
